/**
 * InvestorStrip - "Backed by Y Combinator, the investors behind" over a
 * rolling window of household-name YC portfolio logos, pinned to the bottom
 * band of the hero.
 *
 * WHY IT EXISTS. The page had no third-party signal above the fold and the
 * buyer (a practice owner or office manager) mostly does not know what Y
 * Combinator is. The row borrows recognition from brands they do know. The
 * sentence names Y Combinator as its subject so it stays literally true: the
 * logos are YC's portfolio, not Altair's customers or investors.
 *
 * RULES (founder decisions, 2026-09-19):
 * - Brands and order come from INVESTOR_BRANDS; do not add a brand a doctor
 *   would not recognise on sight.
 * - Official artwork only, bytes unaltered, self-hosted under /investors
 *   (production CSP is img-src 'self'). Never recolour or restyle a mark.
 * - Nothing here is a link. Linking would imply a relationship that does not
 *   exist and would leak the visitor before the pitch.
 * - Full colour at 0.92 opacity. Recognition is the whole job.
 * - The logos roll through a window exactly as wide as the sentence, with
 *   both ends faded, the same mechanism as the EMR and payer strips. Seven
 *   logos side by side read as spam; three at a time read as a signal. On
 *   phones the sentence wraps to two narrow lines, so the window takes the
 *   phone's width there (two logos in view).
 * - The YC mark stands in for the "Y" of "Y Combinator"; a visually hidden
 *   "Y" sits beside it so text content, find-in-page, copy and assistive
 *   tech all still get the full name.
 *
 * LAYOUT. Absolutely positioned at the bottom of the hero, above the aurora
 * glows (z 0) and the Constellation canvas (z 1), level with the content
 * column (z 2). HeroSection reserves the room with its bottom padding, so on
 * a short viewport the hero grows past 100svh and the band never overlaps
 * the CTA. The inner column shrink-wraps to the sentence; the window
 * stretches to that width and uses `contain: inline-size` so the marquee
 * track (far wider) cannot widen it. The static list stays in the DOM
 * visually hidden so screen readers get a real list; the moving copy is
 * aria-hidden so nothing is read twice.
 *
 * MOTION. The marquee track carries `.home-anim` via Marquee, so it pauses
 * under reduced motion on phones per the theme rule; there the strip swaps to
 * the wrapped static list at the phone's full width (three rows), and
 * HeroSection reserves the taller band.
 *
 * No `.landing-reveal` here: the band is above the fold and must be visible
 * in the prerendered HTML.
 */

import { Marquee } from "./Marquee";
import { INVESTOR_BRANDS, INVESTOR_STRIP_LINE } from "./home-data";
import type { InvestorBrand } from "./home-data";

/* Page ink at 0.7 alpha for the sentence. Kept literal like the hero's own
   colours so the first-paint `dark` class flip cannot touch it. */
const EYEBROW_COLOR = "hsl(225 45% 14% / 0.7)";
const LOGO_OPACITY = 0.92;
const MARK_SIZE = 18;

/* One logo. `width`/`height` attributes carry the aspect ratio so the box is
   sized before the SVG decodes; the rendered size follows `--logo-scale`. */
function BrandLogo({
  brand,
  decorative,
}: {
  brand: InvestorBrand;
  decorative?: boolean;
}) {
  return (
    <img
      src={brand.src}
      alt={decorative ? "" : brand.name}
      width={brand.width}
      height={brand.height}
      style={{
        display: "block",
        height: `calc(${brand.height}px * var(--logo-scale, 1))`,
        width: "auto",
        opacity: LOGO_OPACITY,
      }}
    />
  );
}

export function InvestorStrip() {
  const { before, after, second } = INVESTOR_STRIP_LINE;
  return (
    <div
      className="hero-investors"
      role="group"
      aria-label="Companies backed by Y Combinator"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2,
        display: "flex",
        justifyContent: "center",
        padding: "0 24px 40px",
      }}
    >
      {/* Inner column: shrink-wraps to the sentence, which sets the window. */}
      <div
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: "100%",
        }}
      >
        {/* The sentence. The YC mark replaces the "Y" of "Y Combinator". Two
            spans so the phone break lands after the comma. */}
        <p
          className="hero-investors-eyebrow"
          style={{
            margin: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: 14,
            lineHeight: 1.4,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: EYEBROW_COLOR,
            whiteSpace: "nowrap",
          }}
        >
          <span
            className="hero-investors-l1"
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            {before}{" "}
            <img
              src="/investors/yc.svg"
              alt=""
              width={MARK_SIZE}
              height={MARK_SIZE}
              style={{ display: "block", flexShrink: 0, borderRadius: 2 }}
            />
            {/* The letter the mark stands in for, kept in the text for
                assistive tech, find-in-page, copy and crawlers; the mark
                itself is decorative so nothing announces "image". */}
            <span className="hero-investors-y">Y</span> {after}
          </span>
          {/* A real space between the halves for text content and screen
              readers. Flex ignores whitespace-only text nodes, so it does not
              render; the visual gap is the l2 margin. */}{" "}
          <span className="hero-investors-l2">{second}</span>
        </p>

        {/* The window: as wide as the sentence, logos rolling through it. */}
        <div className="hero-investors-window" aria-hidden="true">
          <Marquee durationSeconds={30} direction="left">
            {INVESTOR_BRANDS.map((b) => (
              <span
                key={b.name}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0 24px",
                }}
              >
                <BrandLogo brand={b} decorative />
              </span>
            ))}
          </Marquee>
        </div>

        {/* Static list for assistive tech (visually hidden), and the visible
            fallback under reduced motion on phones. */}
        <ul className="hero-investors-row" role="list">
          {INVESTOR_BRANDS.map((b) => (
            <li key={b.name} style={{ display: "flex", alignItems: "center" }}>
              <BrandLogo brand={b} />
            </li>
          ))}
        </ul>
      </div>

      {/* Scoped styles: the phone switch needs media queries, which inline
          styles cannot express. theme.css is single-writer. */}
      <style>{`
        .hero-investors-l2 {
          margin-left: 0.6ch;
        }
        .hero-investors-window {
          align-self: stretch;
          contain: inline-size;
          margin-top: 24px;
        }
        .hero-investors-y {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip-path: inset(50%);
          white-space: nowrap;
        }
        /* Visually hidden, still in the accessibility tree. */
        .hero-investors-row {
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          padding: 0;
          overflow: hidden;
          clip-path: inset(50%);
          white-space: nowrap;
          list-style: none;
        }
        @media (max-width: 1023px) {
          .hero-investors {
            --logo-scale: 0.9;
          }
        }
        @media (max-width: 768px) {
          .hero-investors {
            --logo-scale: 0.78;
            padding: 0 16px 32px !important;
          }
          .hero-investors-eyebrow {
            flex-direction: column;
            gap: 6px;
            font-size: 12.5px !important;
            letter-spacing: 0.16em !important;
            white-space: normal !important;
            text-align: center;
          }
          .hero-investors-l2 {
            margin-left: 0;
          }
          /* The two-line phone sentence is only ~220px wide; a window that
             narrow shows one logo at a time, so on phones the window takes
             the phone's width instead (two logos in view). */
          .hero-investors-window {
            align-self: auto;
            width: calc(100vw - 32px);
            max-width: 360px;
          }
        }
        /* Reduced motion on phones pauses the marquee (theme rule), so show
           the wrapped static list instead. It uses the phone's full width
           (not the sentence width) so it wraps to three rows, which is what
           HeroSection's reduced-motion bottom padding reserves. */
        @media (max-width: 768px) and (prefers-reduced-motion: reduce) {
          .hero-investors-window {
            display: none;
          }
          .hero-investors-row {
            position: static;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            gap: 14px 26px;
            width: calc(100vw - 32px);
            max-width: 360px;
            height: auto;
            margin: 20px 0 0;
            overflow: visible;
            clip-path: none;
            white-space: normal;
          }
        }
      `}</style>
    </div>
  );
}
